import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

interface BonLaboratoire {
  id: string;
  patient_id: string;
  type_analyse: string;
  date_prescription: string;
  statut: string;
  urgences: boolean;
  patients: { nom: string; prenom: string } | null;
}

export default function SageFemmeDashboardPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sageFemmeId, setSageFemmeId] = useState('');
  const [bons, setBons] = useState<BonLaboratoire[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, en_attente: 0, termine: 0 });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'sage_femme' && role !== 'super_admin') { navigate('/', { replace: true }); return; }
    loadData();
  }, [user, authLoading, role]);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data: sfData } = await supabase
        .from('sage_femmes').select('id').eq('user_id', user!.id).maybeSingle();
      if (!sfData) { setLoading(false); return; }
      setSageFemmeId(sfData.id);

      const { data: bonsData, error } = await supabase
        .from('bons_laboratoire')
        .select('id, patient_id, type_analyse, date_prescription, statut, urgences, patients!inner(nom, prenom)')
        .eq('sage_femme_id', sfData.id)
        .eq('prescripteur_type', 'sage_femme')
        .order('created_at', { ascending: false });

      if (!error && bonsData) {
        setBons(bonsData as BonLaboratoire[]);
        const s = { total: bonsData.length, en_attente: 0, termine: 0 };
        bonsData.forEach((b: BonLaboratoire) => {
          if (b.statut === 'en_attente' || b.statut === 'en_cours') s.en_attente++;
          else if (b.statut === 'termine') s.termine++;
        });
        setStats(s);
      }
    } catch (err) { console.error('Erreur chargement:', err); }
    finally { setLoading(false); }
  };

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getStatutBadge = (statut: string) => {
    switch (statut) {
      case 'en_attente': return <span className="px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-semibold">En attente</span>;
      case 'en_cours': return <span className="px-2.5 py-1 bg-clinic-blue/10 text-clinic-blue-dark rounded-full text-xs font-semibold">En cours</span>;
      case 'termine': return <span className="px-2.5 py-1 bg-green-50 text-green-700 rounded-full text-xs font-semibold">Terminé</span>;
      default: return <span className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold">{statut}</span>;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin"></i>
      </div>
    );
  }

  const statItems = [
    { label: 'Total bons', value: stats.total },
    { label: 'En cours / attente', value: stats.en_attente },
    { label: 'Résultats disponibles', value: stats.termine },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFB] flex flex-col">
      <Header />
      <main className="flex-1 mx-auto max-w-6xl w-full px-4 py-8 sm:px-6 lg:px-10">
        {/* Titre */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">Espace Sage-Femme</h1>
            <p className="mt-1 text-sm text-gray-500">Consultations prénatales et bons de laboratoire</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/medecin/recherche-patients"
              className="rounded-lg border border-clinic-blue/30 bg-white px-4 py-2.5 text-sm font-semibold text-clinic-blue-dark transition-colors hover:bg-clinic-blue/5 whitespace-nowrap"
            >
              Dossiers patients
            </Link>
            <Link
              to="/sage-femme/bon-laboratoire"
              className="rounded-lg bg-clinic-blue px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-clinic-blue-dark whitespace-nowrap"
            >
              Nouveau bon de laboratoire
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {statItems.map((stat) => (
            <div key={stat.label} className="rounded-xl border border-gray-100 bg-white px-5 py-4">
              <p className="text-2xl font-bold text-clinic-blue-dark md:text-3xl">{stat.value}</p>
              <p className="mt-1 text-xs text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Liste des bons */}
        <div className="mb-4 flex items-center gap-3">
          <h2 className="text-base font-semibold text-gray-900">Mes bons de laboratoire</h2>
          <span className="h-px flex-1 bg-gray-100"></span>
        </div>

        {bons.length === 0 ? (
          <div className="rounded-xl border border-gray-100 bg-white p-16 text-center">
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Aucun bon de laboratoire</h3>
            <p className="text-gray-500 mb-6">Vous n'avez pas encore créé de bon de laboratoire.</p>
            <Link
              to="/sage-femme/bon-laboratoire"
              className="inline-block rounded-lg bg-clinic-blue px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-clinic-blue-dark whitespace-nowrap"
            >
              Créer un bon
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {bons.map((bon) => (
              <div key={bon.id} className={`rounded-xl border px-5 py-4 ${bon.urgences ? 'border-red-200 bg-red-50/40' : 'border-gray-100 bg-white'}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-semibold text-gray-900">{bon.patients?.nom} {bon.patients?.prenom}</h3>
                      {bon.urgences && <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-bold">URGENT</span>}
                    </div>
                    <p className="text-sm text-clinic-blue-dark font-medium">{bon.type_analyse}</p>
                    <p className="text-xs text-gray-400 mt-1">Prescrit le {formatDate(bon.date_prescription)}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    {getStatutBadge(bon.statut)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}